export enum GiftRuleType {
  fillin = 'fillin',
  accumulated_fillin = 'accumulated_fillin',
  clube_certo = 'clube_certo',
  pix_payment = 'pix_payment',
  unique_code = 'unique_code',
  prediction_winner = 'prediction_winner',
  sale = 'sale',
  accumulated_sale = 'accumulated_sale',
  customer_registered = 'customer_registered',
  registration_completed = 'registration_completed',
  roleta_prize = 'roleta_prize',
  /**
   * Troca de pontos por número da sorte: o cliente PAGA em pontos e recebe os
   * `drawing_number` do brinde. Exclusiva no brinde — conviver com uma regra de
   * abastecimento seria "ganha de graça OU pagando" no mesmo produto.
   *
   * Quem executa é o pontuax-backend
   * (`drawings-controller.luckyNumberExchangeByPoints`), não a gifts-api:
   * débito, claim do número e conquista saem no MESMO commit.
   */
  points_exchange = 'points_exchange',
}

export namespace GiftRuleType {
  export const translateType = (type: GiftRuleType): string => {
    switch (type) {
      case GiftRuleType.fillin:
        return "Abastecimento";
      case GiftRuleType.accumulated_fillin:
        return "Abastecimentos Acumulados";
      case GiftRuleType.clube_certo:
        return "Clube Certo";
      case GiftRuleType.pix_payment:
        return "Pagamento por Pix";
      case GiftRuleType.unique_code:
        return "Divulgação";
      case GiftRuleType.prediction_winner:
        return "Acertou Bolão";
      case GiftRuleType.sale:
        return "Compra de Produtos";
      case GiftRuleType.accumulated_sale:
        return "Compras Acumuladas";
      case GiftRuleType.customer_registered:
        return "Fez o cadastro";
      case GiftRuleType.registration_completed:
        return "Completou o cadastro";
      case GiftRuleType.roleta_prize:
        return "Prêmio da Roleta";
      case GiftRuleType.points_exchange:
        return "Troca por Pontos";
      default:
        return "";
    }
  };

  /** Regras de abastecimento. */
  export const fillinTypes = [
    GiftRuleType.fillin,
    GiftRuleType.accumulated_fillin,
  ];

  /** Regras de compra de produtos. */
  export const saleTypes = [
    GiftRuleType.sale,
    GiftRuleType.accumulated_sale,
  ];

  /**
   * Regras dirigidas por evento (abastecimento ou compra). São as que podem
   * coexistir entre si; o complemento (clube_certo, pix_payment, unique_code,
   * prediction_winner) é exclusivo.
   */
  export const eventTypes = [...fillinTypes, ...saleTypes];

  /**
   * Regras de cadastro. Não têm posto, combustível nem valor: a conquista sai
   * uma única vez, quando o cliente se cadastra ou completa o perfil.
   */
  export const registrationTypes = [
    GiftRuleType.customer_registered,
    GiftRuleType.registration_completed,
  ];

  export const isSaleType = (type?: GiftRuleType | string): boolean =>
    saleTypes.includes(type as GiftRuleType);

  export const isRegistrationType = (type?: GiftRuleType | string): boolean =>
    registrationTypes.includes(type as GiftRuleType);

  export const isEventType = (type?: GiftRuleType | string): boolean =>
    eventTypes.includes(type as GiftRuleType);

  /**
   * Regras sem posto: quem dispara é rotina própria, pagamento ou cadastro, não
   * um abastecimento/compra num posto. Fonte única da validação do modal e do
   * wizard — lista duplicada já deixou "Fez o cadastro" travar o wizard.
   */
  export const stationlessTypes = [
    GiftRuleType.clube_certo,
    GiftRuleType.pix_payment,
    GiftRuleType.unique_code,
    GiftRuleType.prediction_winner,
    GiftRuleType.roleta_prize,
    GiftRuleType.points_exchange,
    ...registrationTypes,
  ];

  export const requiresStations = (type?: GiftRuleType | string): boolean =>
    !stationlessTypes.includes(type as GiftRuleType);

  /**
   * Regras que o cliente dispara PAGANDO. O preço é do brinde inteiro, então
   * nenhuma delas convive com outra regra no mesmo brinde. Espelha
   * PAYMENT_RULE_TYPES do backend (src/models/giftRuleType.ts).
   */
  export const paymentTypes = [
    GiftRuleType.pix_payment,
    GiftRuleType.points_exchange,
  ];

  export const isPaymentType = (type?: GiftRuleType | string): boolean =>
    paymentTypes.includes(type as GiftRuleType);

  export const giftRuleTypeChoices = Object.values(GiftRuleType)
    .filter(x => typeof x === "string")
    .map((type) => ({
      id: type,
      name: GiftRuleType.translateType(type as GiftRuleType),
    }));
}
